import { createTheme, Theme } from '@mui/material/styles';

type PaletteMode = 'light' | 'dark';

/**
 * TimeLynxx Brand Colors
 * Primary: #2C97A3 (teal)
 * Secondary: #FF6B35 (orange)
 */
const brandColors = {
  primary: {
    main: '#2C97A3',
    light: '#5CBCC9',
    dark: '#1E6B73',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#FF6B35',
    light: '#FF8E64',
    dark: '#E54A1F',
    contrastText: '#FFFFFF',
  },
};

/**
 * Create TimeLynxx theme with light/dark mode support
 */
export const createTimeLynxxTheme = (mode: PaletteMode): Theme => {
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary: brandColors.primary,
      secondary: brandColors.secondary,
      background: {
        default: isLight ? '#F8F9FA' : '#121212',
        paper: isLight ? '#FFFFFF' : '#1E1E1E',
      },
      text: {
        primary: isLight ? '#212529' : '#FFFFFF',
        secondary: isLight ? '#6C757D' : '#B3B3B3',
      },
      divider: isLight ? '#DEE2E6' : '#2D2D2D',
      grey: {
        50: '#F8F9FA',
        100: '#E9ECEF',
        200: '#DEE2E6',
        300: '#CED4DA',
        400: '#ADB5BD',
        500: '#6C757D',
        600: '#495057',
        700: '#343A40',
        800: '#212529',
        900: '#0C0D0E',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontStyle: 'italic',
        fontSize: '2.5rem',
      },
      h2: {
        fontWeight: 700,
        fontStyle: 'italic',
        fontSize: '2rem',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      h5: {
        fontWeight: 500,
        fontSize: '1.25rem',
      },
      h6: {
        fontWeight: 500,
        fontSize: '1rem',
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 400,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.43,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: brandColors.primary.main,
            boxShadow: 'none',
            borderBottom: `1px solid ${isLight ? '#DEE2E6' : '#2D2D2D'}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? '#FFFFFF' : '#1E1E1E',
            borderRight: `1px solid ${isLight ? '#DEE2E6' : '#2D2D2D'}`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 500,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${isLight ? '#DEE2E6' : '#2D2D2D'}`,
            boxShadow: isLight 
              ? '0 2px 4px rgba(0,0,0,0.05)' 
              : '0 2px 4px rgba(0,0,0,0.25)',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 0',
            '&.Mui-selected': {
              backgroundColor: `${brandColors.primary.main}1A`,
              '&:hover': {
                backgroundColor: `${brandColors.primary.main}24`,
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
            color: isLight ? brandColors.primary.main : brandColors.primary.light,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });
};

// Export theme variants for easy reference
export const lightTheme = createTimeLynxxTheme('light');
export const darkTheme = createTimeLynxxTheme('dark');